import { c as _c } from "react/compiler-runtime";
import { AnimatePresence, motion } from 'framer-motion';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import EnvironmentBanner from '@/components/environment-banner';
import usePermissions from '@/hooks/use-permissions';
import useSession from '@/hooks/use-session';
import AssumeBanner from '../assume-banner';
import AuditorBanner from '../auditor-banner';
export type BannersProps = {
  hideEnvironmentBanner?: boolean;
};
const ENV_BANNER_HIDDEN_PREFIXES = ['/ask-percy', '/chat/', '/investigations/', '/playbooks/', '/agents', '/settings', '/internal',
// Onboarding trace runs in FullLayout with its own header SandboxToggle, so the env banner is redundant.
'/users/[id]/onboardings/[onboarding_id]/trace', '/businesses/[id]/onboardings/[onboarding_id]/trace'];

// Tracks each banner's rendered height as a CSS variable on :root so absolutely / fixed-positioned
// elements (e.g. the sidebar drawer in `components/sidebar/sidebar.tsx`) can offset themselves below
// the banner. Without this, drawers anchor under the banner and overlap it.
const useTrackBannerHeight = (cssVarName, isVisible) => {
  const $ = _c(4);
  const ref = useRef(null);
  let t0;
  let t1;
  if ($[0] !== cssVarName || $[1] !== isVisible) {
    t0 = () => {
      if (!isVisible) {
        document.documentElement.style.removeProperty(cssVarName);
        return;
      }
      const el = ref.current;
      if (!el) {
        return;
      }
      const setHeight = () => {
        document.documentElement.style.setProperty(cssVarName, `${el.offsetHeight}px`);
      };
      setHeight();
      const observer = new ResizeObserver(setHeight);
      observer.observe(el);
      return () => {
        observer.disconnect();
        document.documentElement.style.removeProperty(cssVarName);
      };
    };
    t1 = [cssVarName, isVisible];
    $[0] = cssVarName;
    $[1] = isVisible;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  return ref;
};
export const Banners = t0 => {
  const $ = _c(16);
  const {
    hideEnvironmentBanner
  } = t0;
  const router = useRouter();
  const {
    pathname
  } = router;
  const {
    data
  } = useSession();
  const {
    isAuditor
  } = usePermissions();
  const isAssumedSession = !!data?.user?.isAssumedSession;
  let t1;
  if ($[0] !== hideEnvironmentBanner || $[1] !== pathname) {
    t1 = !hideEnvironmentBanner && !ENV_BANNER_HIDDEN_PREFIXES.some(prefix => pathname.startsWith(prefix));
    $[0] = hideEnvironmentBanner;
    $[1] = pathname;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const shouldShowEnvironmentBanner = t1;
  const assumeBannerRef = useTrackBannerHeight("--assume-banner-height", isAssumedSession);
  const envBannerRef = useTrackBannerHeight("--environment-banner-height", shouldShowEnvironmentBanner);
  const auditorBannerRef = useTrackBannerHeight("--auditor-banner-height", isAuditor);
  let t2;
  if ($[3] !== auditorBannerRef || $[4] !== isAuditor) {
    t2 = isAuditor && <AnimatePresence initial={false}><motion.div ref={auditorBannerRef} className="w-full border-b border-tertiary z-0" initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: "auto",
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }}><AuditorBanner /></motion.div></AnimatePresence>;
    $[3] = auditorBannerRef;
    $[4] = isAuditor;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== assumeBannerRef || $[7] !== isAssumedSession) {
    t3 = isAssumedSession && <AnimatePresence initial={false}><motion.div ref={assumeBannerRef} className="w-full border-b border-tertiary z-0" initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: "auto",
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }}><AssumeBanner /></motion.div></AnimatePresence>;
    $[6] = assumeBannerRef;
    $[7] = isAssumedSession;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== envBannerRef || $[10] !== shouldShowEnvironmentBanner) {
    t4 = shouldShowEnvironmentBanner && <AnimatePresence initial={false}><motion.div ref={envBannerRef} className="w-full border-b border-tertiary z-0" initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: "auto",
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }}><EnvironmentBanner /></motion.div></AnimatePresence>;
    $[9] = envBannerRef;
    $[10] = shouldShowEnvironmentBanner;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t2 || $[13] !== t3 || $[14] !== t4) {
    t5 = <>{t2}{t3}{t4}</>;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
};